import React, { useEffect, useState, useRef } from 'react';
import {
  ArrowButton,
  Button,
  Text,
  TextInput,
} from '@alj-react/ui-components';
import LoadingSpinner from '../molecules/LoadingSpinner';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { useLocation } from 'react-router-dom';
import qs from 'qs';
import _ from 'lodash';

import DisplayPriorConsent from './DisplayPriorConsent';
import PlanIdIsAlreadySigned from './PlanIdIsAlreadySigned';
import PlanInvalidStatus from './PlanInvalidStatus';
import DisplayConfirmation from './DisplayConfirmation';
import DisplaySignInput from './DisplaySignInput';
import DisplayError from './DisplayError';
import DisplayFinish from './DisplayFinish';
import { ArrowButtonStyled, BottomNavDiv } from './ESignature.styles';
import {
  getComponent,
  getSignType,
  getTransitionBtnTxt,
  URL_API,
  sendToApi,
} from '../../utils/multi';
import { Context } from '../../utils/ESignatureContext';
import { setBDisplaySignInputSign,
  setAuraXmlOutputSign, setIsLoadingSign,
  setSignatureSign, setCheckboxSelectedSign,
  setBDisplayConfirmationSign, setDataToDisplaySign,
  setBDisplayErrorSign, setQueryParametersSign,
  setCurrentPageToDisplaySign, setIsDoingAuraStuffSign, setApiResultSign,
  setInputFormContentSign, setFromMulti, setDataToDisplaySignBeforeChange,
  setBizCheckStatusSign,
} from '../../utils/dataFetchers';
import { isJSONObject } from '../../utils/utils';
import lodash from 'lodash';

const messages = defineMessages({
  submitTxt: {
    id: 'ESignature.submitTxt',
    defaultMessage: 'Submit',
  },
  pleaseSignTxt: {
    id: 'ESignature.pleaseSignTxt',
    defaultMessage: 'Please sign in the space below',
  },
  clearSignBtnTxt: {
    id: 'ESignature.clearSignBtnTxt',
    defaultMessage: 'Erase',
  },
  saveSignBtnTxt: {
    id: 'ESignature.saveSignBtnTxt',
    defaultMessage: 'Save',
  },
  confirmScreenBtnTxt: {
    id: 'ESignature.confirmScreenBtnTxt',
    defaultMessage: 'Go to confirmation screen',
  },
  thankYouTxt1: {
    id: 'ESignature.thankYouTxt1',
    defaultMessage: 'The submission of your signature has been completed successfully.',
  },
  thankYouTxt2: {
    id: 'ESignature.thankYouTxt2',
    defaultMessage: 'Thank you for taking out our insurance.\nThe person in charge will contact you in the future.',
  },
  errorTxt: {
    id: 'ESignature.errorTxt',
    defaultMessage: 'An error happened. Please contact the agent.',
  },
  confirmationTitle: {
    id: 'ESignature.confirmationTitle',
    defaultMessage: 'Confirmation of signature',
  },
  pleaseCheckTxt: {
    id: 'ESignature.pleaseCheckTxt',
    defaultMessage: 'The following signature will be submitted. Please check it as it cannot be edited once submitted.',
  },
  backToSignBtnTxt: {
    id: 'ESignature.backToSignBtnTxt',
    defaultMessage: 'Back',
  },
  signReqTxt: {
    id: 'ESignature.signReqTxt',
    defaultMessage: 'Signature request',
  },
  authSubmitBtnTxt: {
    id: 'ESignature.authSubmitBtnTxt',
    defaultMessage: 'Procedure Start',
  },
  goToSignBtnTxt: {
    id: 'ESignature.goToSignBtnTxt',
    defaultMessage: 'Go to Sign screen',
  },
  goBackToDisplayDataBtnTxt: {
    id: 'ESignature.goBackToDisplayDataBtnTxt',
    defaultMessage: 'Go back to contract display',
  },
  turnInLandscapeTxt: {
    id: 'ESignature.turnInLandscapeTxt',
    defaultMessage: 'Please turn your device in landscape orientation',
  },
  goToPrevPageBtnTxt: {
    id: 'ESignature.goToPrevPageBtnTxt',
    defaultMessage: 'Back',
  },
  goToNextPageBtnTxt: {
    id: 'ESignature.goToNextPageBtnTxt',
    defaultMessage: 'Next',
  },
  alreadySignedTxt: {
    id: 'ESignature.alreadySignedTxt',
    defaultMessage: 'The Plan ID {planId} has already been signed.\nYou can close this page safely.',
  },
  clickToEditSign: {
    id: 'ESignature.clickToEditSign',
    defaultMessage: '「Click to edit」',
  },
  clickToSign: {
    id: 'ESignature.clickToSign',
    defaultMessage: '「Click here to sign」',
  },
  closeSignBtnTxt: {
    id: 'ESignature.closeSignBtnTxt',
    defaultMessage: 'Close',
  },
  priorConsentBtnTxt: {
    id: 'ESignature.priorConsentBtnTxt',
    defaultMessage: 'I accept',
  },
  errorFormValidationTxt: {
    id: 'ESignature.errorFormValidationTxt',
    defaultMessage: 'There is some input errors, please check.',
  },
  errorDataNotPrepared: {
    id: 'ESignature.errorDataNotPrepared',
    defaultMessage: "Because the policyholder's procedure is not yet complete, the insured's procedure cannot be initiated. Please wait until the policyholder's procedure is complete.",
  },
});

type ESignatureProps = { fetch?: typeof window.fetch };

type AuthParam = {
  contents: {
    type: string,
    value: any,
  }[],
  secrets: {[key: string]: {
    label: string,
    placeholder: string,
  }}
}

const ESignatureBase: React.FC<ESignatureProps> = ({ fetch = window.fetch, children }) => {
  const { dispatch, state } = React.useContext(Context);
  const { formatMessage } = useIntl();

  const [bDisplayFinish, setBDisplayFinish] = useState<boolean>(false);
  const [planIdIsAlreadySigned, setPlanIdIsAlreadySigned] = useState<boolean>(false);
  const [planIdIsInvalidated, setPlanIdIsInvalidated] = useState<boolean>(false);
  const [planIdIsTimesOut, setPlanIdIsTimesOut] = useState<boolean>(false);
  const [planIdInvalidatedMessage, setPlanIdInvalidatedMessage] = useState<boolean>(false);
  const [bDisplayInvalidStatusMessage, setBDisplayInvalidStatusMessage] = useState<boolean>(false);
  const [bDisplayErrorFormValidation, setBDisplayErrorFormValidation] = useState<boolean>(false);
  const [dataNotPrepared, setDataNotPrepared] = useState<boolean>(false);
  const [requiredAuth, setRequiredAuth] = useState<AuthParam>();
  const [isAuth, setIsAuth] = useState<boolean>(false);
  const [authLoading, setAuthLoading] = useState<boolean>(false);
  const [authParam, setAuthParam] = useState<{[key: string] : string}>();

  const isCancelled = useRef(false);
  // const [apiResult, setApiResult] = useState<ApiResult | undefined>();


  const locationSearch = useLocation().search;

  useEffect(() => {
    return () => {
      console.log('ESignature is unmounted.');
      isCancelled.current = true;
    };
  }, []);

  const getDataToDisplayFromApi = async (token: string, planId: string, emmaBaseUrl?: string, emmaToken?: string) => {
    try {
      setBDisplayErrorFormValidation(false);
      setAuthLoading(true);

      const reqOptions = {
        method: requiredAuth ? 'POST' : 'GET',
        headers: { Authorization: token },
        ...requiredAuth ? {
          body: JSON.stringify({ secrets: authParam ?? {} })
        } : {},
      }

      const response = await fetch(`${URL_API}/data/${planId}`, reqOptions);
      console.log(response);
      // 401 is a valid response so it will not go in catch !
      const responseData = await response.json();
      if (!isCancelled.current) {
        if (responseData.status === 200) {

          if (responseData.isAlreadySigned && emmaBaseUrl && emmaToken) {
            const headerElement = document.querySelector('#axaHeader') as HTMLElement;
            if (headerElement) {
              headerElement.style.display = 'none';
            }
            window.location.href = `${emmaBaseUrl}?token=${emmaToken}`;
          } else {
            setPlanIdIsAlreadySigned(responseData.isAlreadySigned || false);
            setPlanIdIsInvalidated(responseData.isInvalidated || false);
            setPlanIdIsTimesOut(responseData.isTimesOut || false);
            setPlanIdInvalidatedMessage(responseData.invalidateMessage || '');

            if (!responseData.payload) {
              setDataNotPrepared(true);
              setAuthLoading(false);
              return;
            }

            if (responseData.payload.auth) {
              // Require login 
              const auth = responseData.payload.auth as AuthParam;
              setRequiredAuth(auth);
              setAuthLoading(false);
              return;
            }

            // keep the original copy for verification
            setDataToDisplaySignBeforeChange(dispatch, lodash.cloneDeep(responseData.payload));

            // Align "contents" property to be compatible to "Contents"
            if (responseData.payload.contents) {
              responseData.payload.Contents = responseData.payload.contents;
            }

            setDataToDisplaySign(dispatch, responseData.payload);
            setFromMulti(dispatch, responseData.isFromMulti);

            console.log('saved result in local state');
            if (responseData.payload.validitySts === '02') {
              setBDisplayInvalidStatusMessage(true);
            }

            setIsAuth(true);
          }
        } else if (responseData.status === 401) {
          setApiResultSign(dispatch, { msg: 'Unauthorized, please check the token value', status: 401, payload: undefined });
          setBDisplayErrorSign(dispatch, true);
        } else if (responseData.status === 403) {
          setBDisplayErrorFormValidation(true);
        } else {
          setApiResultSign(dispatch, responseData);
          setBDisplayErrorSign(dispatch, true);
        }
      } else {
        console.log('canceled and fetch res is ignored !');
      }
    } catch (error: any) {
      console.error(error);

      if (!isCancelled.current) {
        // Display ERROR;
        setApiResultSign(dispatch, { msg: error.message, status: error.status, payload: undefined });
        setBDisplayErrorSign(dispatch, true);
      } else {
        console.log('canceled and fetch res is ignored !');
      }
    }
    isCancelled.current = false;
    setIsLoadingSign(dispatch, false);
    setAuthLoading(false);
  };

  useEffect(() => {
    const queryParams = qs.parse(locationSearch, { ignoreQueryPrefix: true });
    // Call to get data to display
    console.log('Query params are : ', queryParams);
    if (!queryParams.token) {
      console.log('There is no token in URL query parameter !');
    }
    if (!queryParams.planId) {
      console.log('There is no planId in URL query parameter !');
    }
    if (!queryParams.token || !queryParams.planId) {
      console.log('Parameters are wrong, cannot continue');
      setBDisplayErrorSign(dispatch, true);
      setIsLoadingSign(dispatch, false);
    } else {
      setQueryParametersSign(dispatch, queryParams);
      getDataToDisplayFromApi(queryParams.token as string || '', queryParams.planId as string || '', queryParams.emmaBaseUrl as string || '', queryParams.emmaToken as string || '');
    }
  }, []);

  useEffect(() => {
    if (state.apiResult?.status === 200) {
      // Display OK;
      setBDisplayFinish(true);
      // clean state/vars and close
      setCheckboxSelectedSign(dispatch, false);
      setSignatureSign(dispatch, '');
    } else {
      if (state.apiResult) {
        // Display ERROR;
        setBDisplayErrorSign(dispatch, true);
      }
    }
  }, [state.apiResult]);

  const testFormValid: (displaySign: boolean) => boolean = () => {
    // console.log('Checking the screen inputs...');
    return state.checkboxSelected === true
        && (displaySign ? state.signature !== '' : true);
  }

  const goBackToSign = () => {
    setBDisplayConfirmationSign(dispatch, false);
    setBDisplaySignInputSign(dispatch, true);
    setSignatureSign(dispatch, '');
    setTimeout(() => {
      console.log('dispatch resize once');
      window.dispatchEvent(new Event('resize'));
    }, 1);
    window.scrollTo(0, 0);
  };

  const goToNextPage = async (): Promise<{ success: boolean }> => {
    try {
      const { success } = await checkFormInputsAndSaveInState();
      if (!success) {
        return {success:false};
      }

      setCurrentPageToDisplaySign(dispatch, state.currentPageToDisplay + 1);
      setCheckboxSelectedSign(dispatch, false);
      window.scrollTo(0, 0);

      // necessary to delay a bit otherwise element are not yet in the dom
      setTimeout(() => {
        // reset to default back button in case aura modified it
        const btnPreviousAura = document.getElementById('goToPrevPageAura');
        btnPreviousAura?.parentNode?.removeChild(btnPreviousAura);
        const btnPreviousOrig = document.getElementById('goToPrevPage');
        // console.log(btnPreviousOrig);
        if (btnPreviousOrig) {
          btnPreviousOrig.style.display = 'flex';
        }
      }, 10);
        return {success:true};
    } catch (err) {
      console.error('goToNextPage failed:', err);
        return {success:false};
    }
  };

  const checkFormInputsAndSaveInState = async () :Promise<{ success: boolean; data: BizCheckStatus | undefined }> => {
    // Get form on current page
    const contents = state.dataToDisplay?.Contents;
    if (!contents) return { success: false, data: undefined };

    const page = contents
      ?.find((content: { pageNo: number; pageContents: any[] }) => content.pageNo === state.currentPageToDisplay)
      ?.pageContents.map((pageContent: any, subIdx: number) =>
        getComponent(
          state,
          dispatch,
          restoreFormInputsToForm,
          pageContent,
          "page",
          state.currentPageToDisplay,
          subIdx
        )
      );

    const form = page?.filter((x: any) => x.type === 'form');

    let inputResultList = state.inputResultList;

    for (let x = 0; x < form.length; x++) {
      const formElem = document.getElementById(form[x].props.id) as HTMLFormElement;

      if (!formElem.reportValidity()) {
        // alert('form not valid');
        var list = formElem.querySelectorAll(':invalid');
        // console.log(list);
        setBDisplayErrorFormValidation(true);
        return { success: false, data: undefined };
      } else {
        setBDisplayErrorFormValidation(false);
      }

      const data = new FormData(formElem);
      const dataArr = Array.from(data.entries());
      console.log(dataArr);

      // Check for JSON formatted values, and append to the result array if any.
      const processedDataArr = dataArr.reduce((acc: any, curr: any) => {
        const [, value] = curr;

        acc.push(curr);

        if (!isJSONObject(value)) return acc;

        // In case of JSON formatted values, we will parse and those
        // values and add them to the result array.
        // e.g. [["myJSONFormattedValue", '{"foo":"bar"}']] -> [["myJSONFormattedValue", '{"foo":"bar"}'], ["foo", "bar"]]
        const parsedJSON = JSON.parse(value);

        return acc.concat(Object.entries(parsedJSON));
      }, []);

      console.log('processedDataArr', processedDataArr);

      const inputResult = {
        formKey: form[x].props.id,
        formContents: processedDataArr.map((y: any) => ({
          [y[0]]: y[1],
        })),
      }

      // Remove old entry and put new
      inputResultList = inputResultList.filter((x) => x.formKey !== inputResult.formKey);
      inputResultList.push(inputResult);
    }

    if (contents[0]?.bizCheckType === 1) {
      // inputResultList から meta を構築
      const meta = inputResultList.reduce((acc, item) => {
        item.formContents.forEach((contentObj) => {
          Object.entries(contentObj).forEach(([key, value]) => {
            acc[key] = typeof value === 'string' ? value : String(value);
          });
        });
        return acc;
      }, {} as Record<string, string>);

      const identityCheckParm: IdentityCheckParm = {
        uploadKey: `${meta.agentId}_${meta.sekkeiPlanNo}_${meta.insuredId}_${meta.uploadId}_${meta.outputReportId}`,
        meta: meta,
      };

      const identityCheckResult = await identityCheck(state.queryParameters.token,identityCheckParm);
      if (identityCheckResult !== 'OK'){
        setBizCheckStatusSign(dispatch, identityCheckResult);
        return { success: false, data: identityCheckResult };

      }
    }

    // Save content in state for later
    setInputFormContentSign(dispatch, inputResultList);
    return { success: true, data: undefined };
  };

  type IdentityCheckParm = {
    uploadKey: string;
    meta: Record<string, string>;
  };


  const identityCheck = async (token: string, identityCheckParm: IdentityCheckParm): Promise<BizCheckStatus> => {
    let rst: BizCheckStatus = 'SYSTEM_ERROR';
    try {
      const reqOptions = {
        method: 'POST',
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(identityCheckParm),
      };
      const response = await fetch(`${URL_API}/api/identityCheck`, reqOptions);
      // console.log(response);
      // 401 is a valid response so it will not go in catch !
      const responseData = await response.json();
      if (response.status === 200) {
          if (responseData[0].status ===  "VALIDATED") {
            rst = 'OK';
          } else if (responseData[0].status ===  "TIMESOUT") {
            rst = 'TIMES_OUT';
          } else {
            rst = 'ERROR';
          }
      }
    } catch (error: any) {
      if (!navigator.onLine) {
        rst = 'NETWORK_ERROR';
      }
    }
    return rst;
  };

  const findFormValue = (formKey: string, paramKey: string) => {
    let formValue: string| undefined;
    state.inputResultList.forEach((x) => {
      x.formContents?.forEach(content => {
        Object.keys(content).forEach(key => {
          if (formKey === x.formKey && paramKey === key) {
            formValue = content[key] as string;
          }
        });
      });
    });
    return formValue;
  }

  const restoreFormInputsToForm = () => {
    
    state.inputResultList.forEach((x) => {
      // get form
      // console.log(x);
      const formElem = document.getElementById(x.formKey) as HTMLFormElement;
      // console.log(formElem);
      if (formElem) {
        x.formContents.forEach((y) => {
          // console.log(y);
          // console.log(Object.keys(y));
          const item = formElem.elements.namedItem(Object.keys(y)[0]);
          // console.log(item);
          if (item) {
            (item as HTMLInputElement).value = Object.values(y)[0].toString();
            // Item could be one element or a group of radio buttons, so we will make
            // sure to loop over radio nodes and dispatch change event for the checked one.
            if (Object.prototype.toString.call(item) === '[object RadioNodeList]') {
              (item as RadioNodeList).forEach((i: Node) => {
                if (!(i as HTMLInputElement).checked) return;
                i.dispatchEvent(new Event('change',{bubbles:true,cancelable: true}))
              })
            } else {
              (item as HTMLInputElement).dispatchEvent(new Event('change',{bubbles:true,cancelable: true}))
            }
          }
        });
        // formElem.elements.namedItem(x.formContents.)
      }

    });
  };

  const goToSign = async (): Promise<{ success: boolean }> => {
      const { success } = await checkFormInputsAndSaveInState();
      if (!success) {
        return {success:false};
      }

    // canvas of inktool needs this in order to resize correctly after the first render
    setTimeout(() => {
      console.log('dispatch resize once');
      window.dispatchEvent(new Event('resize'));
    }, 1);
    setBDisplayConfirmationSign(dispatch, false);
    setBDisplaySignInputSign(dispatch, true);
    window.scrollTo(0, 0);

    // necessary to delay a bit otherwise element are not yet in the dom
    setTimeout(() => {
      // reset to default back button in case aura modified it
      const btnPreviousAura = document.getElementById('goToPrevPageAura');
      btnPreviousAura?.parentNode?.removeChild(btnPreviousAura);
      const btnPreviousOrig = document.getElementById('goBackToDisplayData');
      // console.log(btnPreviousOrig);
      if (btnPreviousOrig) {
        btnPreviousOrig.style.display = 'flex';
      }
    }, 10);
    return {success:true};
  };

  const onAuraPage = getSignType('aura', state.dataToDisplay?.Contents, state.currentPageToDisplay) || getSignType('noticeForm', state.dataToDisplay?.Contents, state.currentPageToDisplay);

  const goToPrevPage = (e: React.MouseEvent<HTMLElement>) => {
    setBDisplayErrorFormValidation(false);
    const IsHanyokokuchiFlag =  state?.dataToDisplay?.IsHanyokokuchiFlag;
    const pageElement = document.getElementsByClassName('Text-sc-o0ba9r-0 sc-uVWWZ dA-DklD iSiGll');
    const pageTitle = pageElement.length > 0 ? pageElement[0].innerHTML : null;
    const numberOfPagesToReturn = (pageTitle === '告知' && IsHanyokokuchiFlag) ? 2 : 1;
    console.log('numberOfPagesToReturn is : ' + numberOfPagesToReturn)
    if (onAuraPage && state.auraXmlOutput) {
      setAuraXmlOutputSign(dispatch, '');
    } else {
      setCurrentPageToDisplaySign(dispatch, state.currentPageToDisplay - numberOfPagesToReturn);
      setCheckboxSelectedSign(dispatch, true);
      window.scrollTo(0, 0);
      e.currentTarget.blur();
    }
  };

  const onSubmit = () => {
    setIsLoadingSign(dispatch, true);
    sendToApi(state, dispatch, isCancelled);
  };

  const displaySign = getSignType('sign', state.dataToDisplay?.Contents);
  const isDoingAuraStuffLocal = getSignType('aura', state.dataToDisplay?.Contents, state.currentPageToDisplay) || getSignType('noticeForm', state.dataToDisplay?.Contents, state.currentPageToDisplay);;
  const isFormValid = testFormValid(displaySign);
  const isCheckboxOnPage = getSignType('checkbox', state.dataToDisplay?.Contents, state.currentPageToDisplay);
  const goToPrevPageBtnTxt = getTransitionBtnTxt('goToPrevPageBtnTxt', state.currentPageToDisplay, state.dataToDisplay?.Contents);
  const goToNextPageBtnTxt = getTransitionBtnTxt('goToNextPageBtnTxt', state.currentPageToDisplay, state.dataToDisplay?.Contents);

  useEffect(() => {
    setIsDoingAuraStuffSign(dispatch, isDoingAuraStuffLocal);
  }, [isDoingAuraStuffLocal]);

  useEffect(() => {
    // console.log('useefffect currentPageToDisplay changed !');
    restoreFormInputsToForm();
  }, [state.currentPageToDisplay]);

  useEffect(() => {
    // console.log('useefffect bDisplaySignInput changed !');
    restoreFormInputsToForm();
  }, [state.bDisplaySignInput]);

  if(planIdIsAlreadySigned) return <PlanIdIsAlreadySigned messages={messages} />;
  if(planIdIsTimesOut) return <DisplayError messages={messages} apiResult={state.apiResult} />;
  if(planIdIsInvalidated) return <Text color="axaRed">{planIdInvalidatedMessage || formatMessage(messages.errorDataNotPrepared)}</Text>;
  if(dataNotPrepared) return <Text color="axaRed">{formatMessage(messages.errorDataNotPrepared)}</Text>;
  if(requiredAuth && !isAuth) {
    const queryParams = qs.parse(locationSearch, { ignoreQueryPrefix: true });
    return <>
      {
        requiredAuth.contents?.map((content, idx) => getComponent(state, dispatch, () => {}, content, `auth-content`, idx))
      }
      {
        Object.entries(requiredAuth.secrets).map((secret) => {
          const secretKey = secret[0];
          const secretValue = secret[1];
          
          return <div key={`auth-${secretKey}`}>
            <Text key={`auth-label-${secretKey}`} style={{ transition: 'none',  whiteSpace: 'pre-wrap', paddingTop: '10px', paddingBottom: '10px', }}>{secretValue.label}</Text>
            <TextInput key={`auth-input-${secretKey}`} placeholder={secretValue.placeholder} onChange={(e) => setAuthParam({...authParam, [secretKey]: e.target.value })}></TextInput>
          </div>;
        })
      }
      <Button style={{marginTop: 20}} loading={authLoading} onClick={() => getDataToDisplayFromApi(queryParams.token as string || '', queryParams.planId as string || '') }>{formatMessage(messages.authSubmitBtnTxt)}</Button>
      {
        bDisplayErrorFormValidation && <Text color='shyTomato' style={{ paddingBottom: '30px', fontWeight: 'bold' }}>{formatMessage(messages.errorFormValidationTxt)}</Text>
      }
    </>
  }
  if(state.isLoading) return <LoadingSpinner loading={true}/>;
  if(state.bDisplayError) return  <DisplayError apiResult={state.apiResult} messages={messages} />;
  if(bDisplayFinish) return <DisplayFinish messages={messages} restoreFormInputsToForm={restoreFormInputsToForm}/>;
  if(state.bDisplaySignInput) return <DisplaySignInput displaySign={displaySign} isFormValid={isFormValid} messages={messages} onSubmit={onSubmit} />;
  if(state.bDisplayConfirmation) return <DisplayConfirmation displaySign={displaySign} messages={messages} onSubmit={onSubmit} goBackToSign={goBackToSign} isFormValid={isFormValid} />;
  if(planIdIsAlreadySigned) return <PlanIdIsAlreadySigned messages={messages} />;
  if(!displaySign) return <DisplayPriorConsent apiResult={state.apiResult} messages={messages} isFormValid={isFormValid} isCancelled={isCancelled} isCheckboxOnPage={isCheckboxOnPage} goToPrevPageBtnTxt={goToPrevPageBtnTxt} goToNextPageBtnTxt={goToNextPageBtnTxt} goToNextPage={goToNextPage} goToPrevPage={goToPrevPage} restoreFormInputsToForm={restoreFormInputsToForm} checkFormInputsAndSaveInState={checkFormInputsAndSaveInState}/>;
  if(bDisplayInvalidStatusMessage) return <PlanInvalidStatus />;

  return(
    <>
      {
        state.dataToDisplay?.Contents?.find((content: { pageNo: number, pageContents: any[] }) => content.pageNo === state.currentPageToDisplay)?.pageContents.map(( pageContent: any, subIdx: number) => getComponent(state, dispatch, restoreFormInputsToForm, pageContent, 'page', state.currentPageToDisplay, subIdx, state.inputResultList, goToNextPage))
      }
      {
        bDisplayErrorFormValidation && <Text color='shyTomato' style={{ paddingBottom: '30px', fontWeight: 'bold' }}>{formatMessage(messages.errorFormValidationTxt)}</Text>
      }
      <BottomNavDiv>
        {
          state.currentPageToDisplay !== 1 || onAuraPage && state.auraXmlOutput !== ''
            ? <ArrowButtonStyled iconPosition='left' color='white' onClick={goToPrevPage} id='goToPrevPage' data-testid={'goToPrevPage'}>{goToPrevPageBtnTxt || formatMessage(messages.goToPrevPageBtnTxt)}</ArrowButtonStyled>
            : <div></div>
        }
        {
          (state.currentPageToDisplay + 1) === state.dataToDisplay.Contents?.length
            ? <ArrowButton onClick={async () => {await goToSign();}} disabled={!state.checkboxSelected} style={{ display: (state.isDoingAuraStuff && state.auraXmlOutput === '' ? 'none' : 'inherit') }} data-testid={'goToSign'}>{goToNextPageBtnTxt || formatMessage(messages.goToSignBtnTxt)}</ArrowButton>
            : displaySign
              && <ArrowButton onClick={async () => {await goToNextPage();}} disabled={isCheckboxOnPage ? ((!state.checkboxSelected) || (state.isDoingAuraStuff ? state.auraXmlOutput === '' : false)) : false} style={{ display: (state.isDoingAuraStuff && state.auraXmlOutput === '' ? 'none' : 'inherit') }} data-testid={'goToNextPage'}>{goToNextPageBtnTxt || formatMessage(messages.goToNextPageBtnTxt)}</ArrowButton>
        }
      </BottomNavDiv>
      
    </>
  )
};
export default ESignatureBase;
